/**
 * Add-Always button — opens a popover listing every always hint not yet picked.
 * Click an entry to add it to the active list.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useTracker } from '@/contexts/tracker-context';
import { ALWAYS_HINTS, ALWAYS_KIND_BADGE } from './data/always-hints-data';

export function AlwaysAddButton() {
  const { state, dispatch } = useTracker();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const remaining = useMemo(
    () => ALWAYS_HINTS.filter((h) => !state.alwaysHintNames.includes(h.name)),
    [state.alwaysHintNames]
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleAdd = (name: string) => {
    dispatch({ type: 'ADD_ALWAYS', hintName: name });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={remaining.length === 0}
        title="Add an always hint"
        className="flex items-center justify-center gap-1 py-0 px-1 bg-gray-800/50 hover:bg-gray-700/70 rounded text-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed h-5"
      >
        <Plus size={12} />
      </button>

      {open && remaining.length > 0 && (
        <div className="absolute z-50 top-full left-0 mt-1 w-[260px] max-h-[280px] overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl">
          {remaining.map((hint) => {
            const badge = ALWAYS_KIND_BADGE[hint.kind];
            return (
              <button
                key={hint.name}
                onClick={() => handleAdd(hint.name)}
                className="w-full text-left px-2 py-1 text-sm hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                {hint.img ? (
                  <img src={hint.img} alt="" className="h-5 w-5 object-contain flex-shrink-0" />
                ) : (
                  <span className="h-5 w-5 flex-shrink-0" />
                )}
                <span className="text-white truncate flex-1">{hint.name}</span>
                {badge.label && (
                  <span className={`text-[9px] font-bold leading-none px-1 py-0.5 rounded ${badge.color}`}>
                    {badge.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
